import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {AbstractControl, FormControl, FormGroup} from '@angular/forms';
import {TranslocoService} from '@ngneat/transloco';
import {AbstractComponent} from "@components";
import {
  EventItem,
  EventPaymentMethod,
  EventPaymentSettings,
  EventPaymentType,
  Lookups,
  Race,
  TOAST_STATE
} from "@model";
import {EventPaymentSettingsService, LoaderService, ToastService} from "@services";
import {getLanguageValue, numerical, required} from "@utility";

@Component({
  selector: 'app-event-payment-settings',
  templateUrl: './event-payment-settings.component.html',
  styleUrls: ['./event-payment-settings.component.scss']
})
export class EventPaymentSettingsComponent extends AbstractComponent implements OnInit {

  public static readonly HR_CODE : number = 191;

  @Input() currentPaymentSettings: EventPaymentSettings | undefined;
  @Input() disableEditing: boolean = true;
  @Input() paymentMethods: Lookups[] = [];
  @Input() paymentReminderMethods: Lookups[] = [];
  @Input() paymentTypes: Lookups[] = [];
  @Input() event: EventItem | undefined;

  @Output() paymentSettingsSaved: EventEmitter<EventPaymentSettings> = new EventEmitter<EventPaymentSettings>();

  editForm: boolean = false;

  paymentReminderMethodsOptions: Map<number, string> = new Map<number, string>();

  inputForm = new FormGroup({
    mandatoryPayment: new FormControl(),
    paymentMethod: new FormControl(),
    createBarcode: new FormControl(),
    paymentType: new FormControl(),
    issuingInvoice: new FormControl(),
    validHours: new FormControl(),
    paymentReminder: new FormControl(),
    days: new FormControl(),
    hours: new FormControl(),
    reminderMessage: new FormControl(),
    reminderMethod: new FormControl(),
    displayPaymentStatusForRegistrants: new FormControl(),
  });

  constructor(private paymentSettingsService: EventPaymentSettingsService, private toastService: ToastService,
              public translocoService: TranslocoService, private loaderService: LoaderService,) {
    super();
  }

  ngOnInit() {
    this.initForm();
    this.currentPaymentSettingsChanged();
    if (this.paymentReminderMethods) {
      const optionsMap = new Map<number, string>();
      this.paymentReminderMethods.map((reminderMethod: Lookups) => {
        if (reminderMethod.id) {
          optionsMap.set(reminderMethod.id, this.translocoService.translate("event.payment.settings.reminder.delivery.method." + reminderMethod.id))
        }
      });
      this.paymentReminderMethodsOptions = optionsMap;
    }
    this.inputForm.updateValueAndValidity();
  }

  get paymentTypesOptions() {
    let paymentTypesOptions: Map<number, string> = new Map<number, string>();
    if ((this.paymentTypes?.length ?? 0) > 0) {
      const optionsMap: Map<number, string> = new Map<number, string>();
      for (let i = 0; i < this.paymentTypes!.length; i++) {
        if (this.paymentTypes[i].id === EventPaymentType.ONLINE_PAYMENT && this.inputForm.controls.paymentMethod.value === EventPaymentMethod.TRACEY) {
          optionsMap.set(this.paymentTypes[i].id!, this.translocoService.translate("event.payment.settings.payment.type." + this.paymentTypes[i].id))
        } else if (this.paymentTypes[i].id === EventPaymentType.PAYMENT_BY_BANK_TRANSFER) {
          optionsMap.set(this.paymentTypes[i].id!, this.translocoService.translate("event.payment.settings.payment.type." + this.paymentTypes[i].id))
        }
      }
      paymentTypesOptions = optionsMap
    }
    return paymentTypesOptions;
  }

  private subscribeToMandatoryPaymentFieldChange() {
    this.subscriptions.push(
      this.inputForm.controls.mandatoryPayment.valueChanges.subscribe((value) => {
        if (value === true) {
          this.inputForm.controls.paymentMethod.setValue(EventPaymentMethod.TRACEY);
          this.inputForm.controls.paymentMethod.disable();
          this.inputForm.controls.validHours.removeValidators([required]);
          this.inputForm.controls.validHours.setValue(undefined);
          this.inputForm.controls.validHours.disable();
          this.inputForm.controls.paymentReminder.setValue(undefined);
          this.inputForm.controls.paymentReminder.disable();
          this.inputForm.controls.reminderMessage.setValue(undefined);
          this.inputForm.controls.reminderMethod.setValue(undefined);
          this.inputForm.controls.paymentType.setValue([EventPaymentType.ONLINE_PAYMENT])
          this.inputForm.controls.paymentType.markAsUntouched()
          this.inputForm.controls.paymentType.updateValueAndValidity();
          this.inputForm.controls.paymentType.disable();
        } else {
          this.inputForm.controls.paymentMethod.enable();
          this.inputForm.controls.paymentReminder.enable();
          if (this.inputForm.controls.paymentMethod.value === EventPaymentMethod.TRACEY) {
            this.inputForm.controls.validHours.enable();
            this.inputForm.controls.validHours.addValidators([required]);
          }
        }
      })
    );
  }

  private subscribeToPaymentMethodFieldChange() {
    this.subscriptions.push(
      this.inputForm.controls.paymentMethod.valueChanges.subscribe((value) => {
        if (value === EventPaymentMethod.ORGANISER) {
          this.inputForm.controls.paymentType.setValue([EventPaymentType.PAYMENT_BY_BANK_TRANSFER])
          this.inputForm.controls.paymentType.disable();
          this.setErrorOrganiserPaymentDataMissingError(true);
        }
        if (value === EventPaymentMethod.TRACEY) {
          this.inputForm.controls.paymentType.enable()
          this.inputForm.controls.paymentType.markAsUntouched()
          this.inputForm.controls.paymentType.addValidators([required]);
          this.inputForm.controls.paymentType.updateValueAndValidity();

          this.inputForm.controls.issuingInvoice.enable();
          this.inputForm.controls.issuingInvoice.addValidators([required]);
          this.inputForm.controls.issuingInvoice.updateValueAndValidity();
          this.inputForm.controls.validHours.enable();
          if (!this.inputForm.controls.mandatoryPayment.value) {
            this.inputForm.controls.validHours.addValidators([required]);
            this.inputForm.controls.validHours.updateValueAndValidity();
          }
          this.setErrorOrganiserPaymentDataMissingError(false);
        } else {
          this.inputForm.controls.issuingInvoice.removeValidators([required]);
          this.inputForm.controls.issuingInvoice.setValue(false);
          this.inputForm.controls.issuingInvoice.disable();
          this.inputForm.controls.validHours.removeValidators([required]);
          this.inputForm.controls.validHours.setValue(undefined);
          this.inputForm.controls.validHours.disable();
        }
      })
    );
  }

  private setErrorOrganiserPaymentDataMissingError(isOrganiserPaymentMethod: boolean) {
    if (isOrganiserPaymentMethod) {
      const paymentData = this.event?.organiser?.organiserPaymentData;
      if (!paymentData?.organiserId) {
        this.inputForm.controls.paymentMethod.setErrors({paymentDataMissing: true})
      } else if ((!paymentData?.iban || paymentData?.iban === '')) {
        this.inputForm.controls.paymentMethod.setErrors({paymentDataMissing: 'iban'})
      }
    }
    else {
      const control = this.inputForm.controls.paymentMethod;
      if (control.hasError('paymentDataMissing')) {
        const errors = { ...control.errors };
        delete errors['paymentDataMissing'];
        delete errors['paymentDataMissing'];
        control.setErrors(Object.keys(errors).length ? errors : null);
      }
    }
    this.inputForm.controls.paymentMethod.updateValueAndValidity({emitEvent:false});
  }

  private subscribeToPaymentReminderFieldChange() {
    this.subscriptions.push(this.inputForm.controls.paymentReminder.valueChanges.subscribe(value => {
      if (value === true) {
        this.inputForm.controls.reminderMessage.enable();
        this.inputForm.controls.reminderMessage.addValidators([required]);
        this.inputForm.controls.reminderMessage.markAsUntouched();
        this.inputForm.controls.days.enable();
        this.inputForm.controls.days.addValidators([required]);
        this.inputForm.controls.days.markAsUntouched();
        this.inputForm.controls.hours.enable();
        this.inputForm.controls.hours.addValidators([required]);
        this.inputForm.controls.hours.markAsUntouched();
        this.inputForm.controls.reminderMethod.enable();
        this.inputForm.controls.reminderMethod.addValidators([required]);
        this.inputForm.controls.reminderMethod.markAsUntouched();
      } else {
        this.inputForm.controls.reminderMessage.removeValidators([required]);
        this.inputForm.controls.reminderMessage.setValue(undefined);
        this.inputForm.controls.reminderMessage.disable();
        this.inputForm.controls.days.removeValidators([required]);
        this.inputForm.controls.days.setValue(undefined);
        this.inputForm.controls.days.disable();
        this.inputForm.controls.hours.removeValidators([required]);
        this.inputForm.controls.hours.setValue(undefined);
        this.inputForm.controls.hours.disable();
        this.inputForm.controls.reminderMethod.removeValidators([required]);
        this.inputForm.controls.reminderMethod.setValue(undefined);
        this.inputForm.controls.reminderMethod.disable();
      }
      this.inputForm.controls.reminderMessage.updateValueAndValidity();
      this.inputForm.controls.days.updateValueAndValidity();
      this.inputForm.controls.hours.updateValueAndValidity();
      this.inputForm.controls.reminderMethod.updateValueAndValidity();
    }));
  }

  private subscribeToPaymentTypeFieldChange() {
    this.subscriptions.push(this.inputForm.controls.paymentType.valueChanges.subscribe((types: EventPaymentType[] | undefined) => {
      if (types?.includes(EventPaymentType.PAYMENT_BY_BANK_TRANSFER) && this.event?.country === EventPaymentSettingsComponent.HR_CODE) {
        this.inputForm.controls.createBarcode.enable();
      }
      else {
        this.inputForm.controls.createBarcode.setValue(undefined);
        this.inputForm.controls.createBarcode.disable();
      }
    }));
  }
  private initForm() {
    this.inputForm.controls.validHours.addValidators([numerical]);
    this.inputForm.controls.mandatoryPayment.addValidators([required]);
    this.inputForm.controls.paymentMethod.addValidators([required]);
    this.inputForm.controls.mandatoryPayment.addValidators([
      (control: AbstractControl): { [key: string]: any } | null => {
        const isMandatory = control.value;

        const groupRaces: Race[] = this.event?.racesList?.filter(
            (race: Race) => race?.raceSettings?.enableGroupRegistrationPayment === true
        ) || [];
        if ((groupRaces.length ?? 0) > 0 && !isMandatory) {
          control.markAsTouched();
          const errorMessages = groupRaces.map((race: Race) => getLanguageValue(race.name, race.nameEng, race.nameIta, race.nameSlo, race.nameSr, race.nameBs, this.translocoService.getActiveLang())).join(", ")
          return { groupRegistrationPaymentEnabled: errorMessages};
        }
        return null;
      }
    ])


    this.subscribeToMandatoryPaymentFieldChange();
    this.subscribeToPaymentMethodFieldChange();
    this.subscribeToPaymentReminderFieldChange();
    this.subscribeToPaymentTypeFieldChange();

    if (this.currentPaymentSettings?.eventId) {
      this.inputForm.patchValue(this.currentPaymentSettings);
      this.inputForm.updateValueAndValidity();
    } else {
      this.inputForm.reset();
      this.inputForm.controls.mandatoryPayment.setValue(false);
      this.inputForm.controls.displayPaymentStatusForRegistrants.setValue(true);
    }
    this.subscriptions.push(this.inputForm.valueChanges.subscribe(() => {
      this.inputForm.markAsTouched();
    }));
    if (this.disableEditing) {
      this.inputForm.disable();
    }
  }

  private currentPaymentSettingsChanged() {
    if (this.currentPaymentSettings) {
      this.editForm = true;
    } else if (this.event?.id) {
      this.currentPaymentSettings = new EventPaymentSettings();
      this.currentPaymentSettings.eventId = this.event.id!;
      this.currentPaymentSettings.displayPaymentStatusForRegistrants = true;
      this.editForm = false;
    }
  }


  getPaymentSettingsFields(): EventPaymentSettings {
    this.checkValidators(this.inputForm);
    let paymentSettings: EventPaymentSettings = {...this.currentPaymentSettings}
    paymentSettings.eventId = this.event?.id;
    paymentSettings.mandatoryPayment = this.inputForm.controls.mandatoryPayment.value;
    paymentSettings.paymentMethod = this.inputForm.controls.paymentMethod.value;
    paymentSettings.createBarcode = this.inputForm.controls.createBarcode.value;
    paymentSettings.paymentType = this.inputForm.controls.paymentType.value;
    paymentSettings.issuingInvoice = this.inputForm.controls.issuingInvoice.value;
    paymentSettings.validHours = this.inputForm.controls.validHours.value;
    paymentSettings.paymentReminder = this.inputForm.controls.paymentReminder.value;
    paymentSettings.days = this.inputForm.controls.days.value;
    paymentSettings.hours = this.inputForm.controls.hours.value;
    paymentSettings.reminderMessage = this.inputForm.controls.reminderMessage.value;
    paymentSettings.reminderMethod = this.inputForm.controls.reminderMethod.value;
    paymentSettings.displayPaymentStatusForRegistrants = this.inputForm.controls.displayPaymentStatusForRegistrants.value;
    return paymentSettings;
  }

  onSave() {
    if (!this.inputForm.valid || this.disableEditing) {
      this.markAllAsTouched(this.inputForm);
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("event.item.payment.settings.error.form"))
      return;
    } else {
      let paymentSettings: EventPaymentSettings = this.getPaymentSettingsFields();
      if (paymentSettings !== undefined && paymentSettings.eventId) {
        this.loaderService.startLoading();
        this.paymentSettingsService.updateEventPaymentSettings(paymentSettings, () => {
          this.loaderService.dismissLoading();
          this.toastService.showToast(TOAST_STATE.success,
            this.translocoService.translate("app.common.success.event.payment.settings.edit"))
          this.paymentSettingsSaved.emit(paymentSettings);
        }, () => {
          this.loaderService.dismissLoading();
          this.toastService.showToast(TOAST_STATE.danger,
            this.translocoService.translate("app.common.server.error"))
        });
      }
    }
  }
}
